import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Zap, Menu, Heart, Gavel } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetClose,
} from "@/components/ui/sheet";
import { fetchConfig } from "@/lib/api";

const links = [
    { to: "/?tab=apps", label: "Apps" },
    { to: "/market", label: "Market" },
    { to: "/wallet", label: "Wallet" },
    { to: "/rules", label: "Rules" },
    { to: "/about", label: "About" },
];

export const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const config = useQuery({ queryKey: ["config"], queryFn: fetchConfig });
    const paidOn = !!config.data?.paid_slots_enabled;

    const isActive = (to) => {
        const path = to.split("?")[0];
        if (path === "/market" || path === "/wallet" || path === "/rules" || path === "/about") {
            return location.pathname === path;
        }
        return location.pathname === "/" && location.search.includes("tab=apps");
    };

    return (
        <header className="sticky top-0 z-50 border-b border-border bg-surface-0/95 backdrop-blur supports-[backdrop-filter]:bg-surface-0/80">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-brand-link"
                    className="flex items-center gap-2 group"
                >
                    <span className="grid place-items-center h-9 w-9 rounded-lg bg-brand text-primary-foreground shadow-[0_0_20px_-6px_hsl(var(--lime))]">
                        <Zap className="h-5 w-5" strokeWidth={2.5} />
                    </span>
                    <span className="font-heading text-lg font-bold tracking-tight">
                        outbid<span className="text-brand">.fit</span>
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    {links.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={`px-3 py-2 text-sm rounded-md transition-colors ${
                                isActive(l.to)
                                    ? "text-foreground"
                                    : "text-muted-foreground hover:text-foreground"
                            }`}
                        >
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <Button
                        data-testid="nav-market-button"
                        onClick={() => navigate("/market/new")}
                        className="hidden lg:inline-flex bg-brand text-primary-foreground hover:bg-brand-dim font-semibold"
                    >
                        <Gavel className="h-4 w-4 mr-1.5" /> List a slot
                    </Button>
                    <Button
                        data-testid="nav-list-button"
                        variant="outline"
                        onClick={() => navigate("/list")}
                        className="hidden sm:inline-flex font-semibold"
                    >
                        <Heart className="h-4 w-4 mr-1.5" /> List app
                    </Button>

                    <Sheet>
                        <SheetTrigger asChild>
                            <Button
                                variant="outline"
                                size="icon"
                                className="md:hidden"
                                data-testid="nav-mobile-menu-button"
                            >
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="bg-surface-1 border-border w-72">
                            <div className="flex flex-col gap-1 mt-8">
                                {links.map((l) => (
                                    <SheetClose asChild key={l.to}>
                                        <Link
                                            to={l.to}
                                            data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                            className={`px-3 py-3 text-base rounded-md ${
                                                isActive(l.to)
                                                    ? "bg-secondary text-foreground"
                                                    : "text-muted-foreground"
                                            }`}
                                        >
                                            {l.label}
                                        </Link>
                                    </SheetClose>
                                ))}
                                <SheetClose asChild>
                                    <Button
                                        data-testid="nav-mobile-market-button"
                                        onClick={() => navigate("/market/new")}
                                        className="mt-4 bg-brand text-primary-foreground hover:bg-brand-dim font-semibold"
                                    >
                                        <Gavel className="h-4 w-4 mr-1.5" /> List a slot
                                    </Button>
                                </SheetClose>
                                <SheetClose asChild>
                                    <Button
                                        data-testid="nav-mobile-list-button"
                                        variant="outline"
                                        onClick={() => navigate("/list")}
                                        className="mt-2 font-semibold"
                                    >
                                        <Heart className="h-4 w-4 mr-1.5" /> List app
                                    </Button>
                                </SheetClose>
                                {paidOn ? (
                                    <SheetClose asChild>
                                        <Button
                                            data-testid="nav-mobile-featured-button"
                                            variant="outline"
                                            onClick={() => navigate("/outbid")}
                                            className="mt-2 font-semibold"
                                        >
                                            Featured slot
                                        </Button>
                                    </SheetClose>
                                ) : null}
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};
